import * as v from 'valibot';

import {
  ActivityIndicatorSchema as SharedActivityIndicatorSchema,
  CompletionStatusSchema as SharedCompletionStatusSchema,
  SuccessStatusSchema as SharedSuccessStatusSchema,
} from '@fe-learner-common';
import { ComponentSchema } from '@fe-learner-common/api/schemas/componentSchema';

const SuccessStatusSchema = { ...SharedSuccessStatusSchema };
const CompletionStatusSchema = { ...SharedCompletionStatusSchema };
const ActivityIndicatorSchema = { ...SharedActivityIndicatorSchema };

const ProjectParticipationType = v.union([
  v.literal('NEW'),
  v.literal('NORMAL'),
  v.literal('DELETED'),
  v.literal('QUEUED'),
  v.literal('QUEUED_REJECTED'),
]);

const ProgressTrackingType = v.union([
  v.literal('CLICK'),
  v.literal('INTEGRATION'),
  v.literal('TOGGLE'),
  v.literal('NONE'),
  v.literal('SPECIAL'),
  v.literal('TEMPORAL'),
  v.literal('SELF'),
  v.literal('VISIBLE'),
]);

export const UserAwardSchema = v.object({
  id: v.nullable(v.number()),
  issued: v.string(),
  title: v.nullable(v.string()),
  type: v.union([v.literal('certificate'), v.literal('diploma')]),
});

const ParticipationProductType = v.fallback(
  v.union([
    v.literal('scorm-spp'),
    v.literal('scorm'),
    v.literal('quiz-spp'),
    v.literal('quiz'),
    v.literal('scorequiz-spp'),
    v.literal('scorequiz'),
    v.literal('cloudbreaker-spp'),
    v.literal('unknown'),
  ]),
  'unknown',
);

export const ParticipationDataSchema = v.object({
  userAwards: v.nullable(v.array(UserAwardSchema)),
  userId: v.number(),
  expired: v.boolean(),
  seatNumber: v.nullable(v.number()),
  completionStatus: v.nullable(CompletionStatusSchema),
  projectId: v.number(),
  activated: v.boolean(),
  validToUse: v.boolean(),
  peopleVisible: v.boolean(),
  partsCompleted: v.nullable(v.number()),
  partsTotal: v.nullable(v.number()),
  firstAccess: v.nullable(v.number()),
  participationId: v.nullable(v.string()),
  lastAccess: v.nullable(v.number()),
  activitiesCompleted: v.boolean(),
  firstActivation: v.nullable(v.number()),
  archived: v.boolean(),
  inError: v.boolean(),
  participationOverallStatus: v.nullable(
    v.object({
      completionStatus: v.nullable(CompletionStatusSchema),
      successStatus: v.nullable(SuccessStatusSchema),
      score: v.nullable(v.number()),
      sticky: v.boolean(),
      updated: v.nullable(v.number()),
      updatedBy: v.nullable(v.number()),
      comment: v.nullable(v.string()),
    }),
  ),
  virtualProject: v.nullable(v.boolean()), // could not find this in the API.
  virtualParticipation: v.nullable(v.boolean()), // could not find this in the API.
  impersonateParticipation: v.nullable(v.boolean()), // could not find this in the API.
  impersonatedDisplayName: v.nullish(v.string()),
  guestMode: v.boolean(),
  percentageCompleted: v.nullable(v.number()),
  flirtVisible: v.boolean(),
  onTrack: v.boolean(),
  type: v.nullable(ProjectParticipationType),
  expiration: v.nullable(v.number()),
  productType: v.optional(v.nullable(ParticipationProductType)),
  accessLink: v.optional(v.nullable(v.string())),
});

const ProjectType = v.fallback(
  v.union([
    v.literal('DESIGNED_PROJECT'),
    v.literal('PROJECT_SELECTION'),
    v.literal('SINGLE_PRODUCT_PROJECT'),
    v.literal('MICROLEARNING'),
  ]),
  'DESIGNED_PROJECT',
);

export const ProjectDataSchema = v.object({
  type: v.optional(v.nullable(ProjectType)),
  timezone: v.nullable(v.string()),
  userTitle: v.nullable(v.string()),
  userDescription: v.nullable(v.string()),
  archivable: v.boolean(),
  subtype: v.nullable(v.string()),
  projectId: v.number(),
  thumbnails: v.nullable(v.array(v.string())),
  note: v.nullable(v.string()),
  resourcesVisible: v.boolean(),
  adminAllowed: v.boolean(),
  certificateAvailable: v.boolean(),
  diplomaAvailable: v.boolean(),
  flirtId: v.nullable(v.string()),
  autoIcal: v.boolean(),
  brandingId: v.number(),
  displayTimezone: v.nullable(v.string()),
  displayWaitListDetails: v.boolean(),
  coursetype: v.nullable(v.string()),
  displayUserDescription: v.nullable(v.string()),
  displayUserTitle: v.nullable(v.string()),
  awardAvailable: v.boolean(),
  social: v.boolean(),
  newsFlirtId: v.nullable(v.string()),
  progressVisible: v.boolean(),
  infoPageVisible: v.boolean(),
  name: v.nullable(v.string()),
  country: v.nullable(v.string()),
  locale: v.nullable(v.string()),
  language: v.string(),
  duration: v.optional(v.nullable(v.string())),
  startDate: v.optional(v.nullable(v.string())),
  endDate: v.optional(v.nullable(v.string())),
});

export const ActivityTrail = v.object({
  index: v.nullish(v.number()),
  optional: v.nullish(v.boolean()),
  title: v.nullish(v.string()),
  description: v.nullish(v.string()),
  status: v.nullish(v.string()),
  visible: v.nullish(v.boolean()),
  completionStatus: v.nullish(CompletionStatusSchema),
  successStatus: v.nullish(SuccessStatusSchema),
  pendingEvaluations: v.nullish(v.boolean()),
  activityIndicator: v.nullish(ActivityIndicatorSchema),
  componentMeta: v.nullish(v.array(ComponentSchema)),
  cid: v.nullish(v.string()),
  overdue: v.nullish(v.boolean()),
  progressTrackingType: v.nullish(ProgressTrackingType),
  completedDate: v.nullish(v.number()),
  completeByDate: v.nullish(v.number()),
  discussionsDescription: v.nullish(v.string()),
  discussionsTitle: v.nullish(v.string()),
  flirtTagId: v.nullish(v.string()),
  completed: v.nullish(v.boolean()),
  enabled: v.nullish(v.boolean()),
});

const ActivityTrailPage = v.object({
  title: v.nullish(v.string()),
  completedAndPassed: v.boolean(),
  cid: v.string(),
  activities: v.array(ActivityTrail),
  text: v.nullish(v.string()),
  completed: v.boolean(),
  componentMeta: v.optional(v.array(ComponentSchema)),
  enabled: v.boolean(),
});

const ActivityTrailSegmentedActivity = v.object({
  selectedSegment: v.nullish(v.string()),
  segments: v.nullish(
    v.array(v.object({ title: v.string(), cid: v.string() })),
  ),
  cid: v.nullish(v.string()),
});

export const ActivityTrailMapped = v.object({
  virtualProject: v.boolean(),
  virtualParticipation: v.boolean(),
  impersonateParticipation: v.boolean(),
  impersonatedDisplayName: v.nullish(v.string()),
  guestMode: v.nullish(v.boolean()),
  newResourceCount: v.number(),
  segmentedActivities: v.nullish(v.array(ActivityTrailSegmentedActivity)),
  pages: v.nullish(v.array(ActivityTrailPage)),
  completionStatus: CompletionStatusSchema,
  onTrack: v.boolean(),
  currentActivity: v.nullish(v.string()),
});

export const SessionDataSchema = v.object({
  disenrollPossible: v.boolean(),
  sessionId: v.number(),
});

export const PreviewProjectDataSchema = v.merge([
  ProjectDataSchema,
  v.object({
    accessLink: v.nullable(v.string()),
  }),
]);

export type TProjectData = v.Output<typeof ProjectDataSchema>;
export type TPreviewProjectData = v.Output<typeof PreviewProjectDataSchema>;
export type TSessionData = v.Output<typeof SessionDataSchema>;

export type TActivityTrailMappedData = v.Output<typeof ActivityTrailMapped>;
export type TActivityTrailData = v.Output<typeof ActivityTrail>;
export type TActivityTrailPageData = v.Output<typeof ActivityTrailPage>;
export type TParticipationData = v.Output<typeof ParticipationDataSchema>;
export type TTrailData = v.Output<typeof ActivityTrailMapped>;
export type TTrailActivityData = v.Output<typeof ActivityTrail>;
export const SessionUrlSchema = v.object({
  sessionUrl: v.nullable(v.string()),
});
export type TUserAward = v.Output<typeof UserAwardSchema>;
